import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { UpdateAccountDetailsAction } from '@swf/core/types/Account';
import { scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';
import { getCountry, getInitialOptions } from '@swf/core/utils/phoneVerification';

import type { FormValues } from '../Occupation';
import { MERGED_OCCUPATION } from '../Occupation';

const useOccupation = (notification?: boolean) => {
  const embeddedAccount = useSelector(select.location.isEmbeddedAccount);
  const occupationInfo = useSelector(select.account.getOccupationInfo);
  const mergedOccupation = useSelector(select.account.getWebClientDialog) === MERGED_OCCUPATION;
  const accountInfo = {
    name: useSelector(select.account.getFullName),
    address: useSelector(select.account.getAddress),
    city: useSelector(select.account.getCity),
    zipCode: useSelector(select.account.getZipCode),
    phone: useSelector(select.account.getPhone),
    state: useSelector(select.account.getAddressState)
  };

  const country = useSelector(select.account.getCountry) as string;
  const companyPhoneAndCode = getInitialOptions(occupationInfo?.companyPhone as string, country);
  const personalPhoneAndCode = getInitialOptions(accountInfo.phone as string, country);

  const initialValues: FormValues = {
    ...occupationInfo,
    ...accountInfo,
    occupation: mergedOccupation ? undefined : occupationInfo?.occupation,
    dialingCode: companyPhoneAndCode?.dialingCode,
    countryCode: personalPhoneAndCode?.dialingCode,
    companyPhone: companyPhoneAndCode?.phoneNumber,
    phone: personalPhoneAndCode?.phoneNumber,
    country: getCountry(country)?.text
  } as FormValues;

  const {
    account: { updateAccount, updatePEPHIOSelection, confirmAccountDetails },
    modal
  } = useDispatch<Dispatch>();

  const closeModal = () => {
    if (embeddedAccount) {
      window.close();
    } else {
      modal.close();
    }
  };

  const onSubmit = async ({
    occupation,
    pepHioSelection,
    companyName,
    companyAddress,
    companyPhone,
    pepHioName,
    dialingCode,
    relationshipToPepHio,
    sourceOfFunds,
    sourceOfWealth,
    address,
    city,
    zipCode,
    state,
    countryCode,
    phone
  }: FormValues) => {
    const selectionResponse = await updatePEPHIOSelection({ pepHioSelection, rmf: false });
    scrollToTop();

    let addressObject = {};

    if (notification) {
      addressObject = {
        addr1: address,
        city,
        zipCode,
        state,
        phone: `${countryCode.split(' ')[0]}${phone}`
      };
    }

    if (!selectionResponse.success) {
      return buildFormErrors(selectionResponse);
    }

    const occupationResponse = await updateAccount({
      occupationInfo: {
        occupation,
        companyName,
        companyAddress,
        pepHioName,
        relationshipToPepHio,
        sourceOfFunds,
        sourceOfWealth,
        companyPhone: companyPhone ? `${dialingCode.split(' ')[0]}${companyPhone}` : ''
      },
      ...addressObject
    });

    if (occupationResponse.success && notification) {
      confirmAccountDetails({
        action: UpdateAccountDetailsAction.Update
      }).then(closeModal);
    }

    return buildFormErrors(occupationResponse);
  };

  return { initialValues, onSubmit };
};

export default useOccupation;
